import { FC } from 'react';
import { Drawer } from 'rsuite';
import metadata from 'data/metadata';
import { useAppSelector } from 'hooks/redux';
import { getText } from 'utils/stringUtil';
import CardTitles from 'modules/planRecommender/components/PlanDetailsPopup/CardTitles/CardTitles';
import PlanCardBody from 'modules/planRecommender/components/PlanDetailsPopup/PlanCardBody/PlanCardBody';
import { DEFAULT_TEXT } from 'modules/planRecommender/pages/constants';
import { SUPER_SCRIPTS } from 'modules/benefitsWellness/constants/BenefitConstants';
import TableContextProvider from 'modules/benefitsWellness/components/TableContext/TableContextProvider';
import { baseApi } from 'utils/apiUtil';
import { getCardFieldValue } from 'utils/planRecommenderUtils/planRecommenderUtils';
import iconBack from 'images/icons/icon-back.png';
import {
  FIELD_KEYS,
  HEADER_KEYS,
  RX_TIER_FIELD_KEYS,
  SEE_SBC,
} from 'constants/languageConstants';
import PlanCardBodyMobile from './PlanCardBody/PlanCardBodyMobile/PlanCardBodyMobile';
import styles from './planDetailsPopup.module.less';

type PlanDetailsPopupProps = {
  planType: string;
  plan: any;
  networkType: string;
  selectedRate: string;
  isdrawerOpen: boolean;
  onHide: () => void;
  planId: string;
};

const PlanDetailsPopup: FC<PlanDetailsPopupProps> = (
  props: PlanDetailsPopupProps
) => {
  const { planType, plan, networkType, selectedRate, isdrawerOpen, onHide } =
    props;
  const language = useAppSelector((state) => state.benguide.data?.language);

  const formatFieldValue = (
    showSuperScripts: any,
    key: any,
    network: any,
    selectedRate: any,
    plan: any,
    appendSuperScripts: any,
    clearSuperScripts: any,
    isTooltip = false,
    hasDollar = true,
    shouldLowerCase = true
  ) => {
    const rate = selectedRate;
    const value = getCardFieldValue(
      plan,
      key,
      network,
      rate,
      hasDollar,
      shouldLowerCase
    );

    return showSuperScripts
      ? appendSuperScripts(
          !value || value.toString().trim().length === 0 ? DEFAULT_TEXT : value,
          !isTooltip
        )
      : clearSuperScripts(
          !value || value.toString().trim().length === 0 ? DEFAULT_TEXT : value,
          !isTooltip
        );
  };

  const appendSuperScripts = (value: any, withElement = true) => {
    return SUPER_SCRIPTS.reduce((current: any, superscript: any) => {
      if (value.indexOf(superscript.strValue) >= 0 && withElement) {
        const elements = value.split(superscript.strValue);
        return elements.map((elem: any) =>
          elem && elem.length > 0 ? (
            <>
              {elem}
              <sup>{superscript.superScriptValue}</sup>
            </>
          ) : (
            <></>
          )
        );
      } else {
        if (value.indexOf(superscript.strValue) >= 0 && !withElement) {
          const search = new RegExp(superscript.strValue, 'g');
          return value.replace(search, `(${superscript.superScriptValue})`);
        } else {
          return current;
        }
      }
    }, value);
  };

  const clearSuperScripts = (value: any, withElement = true) => {
    return SUPER_SCRIPTS.reduce((current: any, superscript: any) => {
      if (value.indexOf(superscript.strValue) >= 0 && withElement) {
        const elements = value.split(superscript.strValue);
        return elements.map((elem: any) =>
          elem && elem.length > 0 ? <>{elem}</> : <></>
        );
      } else {
        if (value.indexOf(superscript.strValue) >= 0 && !withElement) {
          const search = new RegExp(superscript.strValue, 'g');
          return value.replace(search, `(${superscript.superScriptValue})`);
        } else {
          return current;
        }
      }
    }, value);
  };

  const getTextByField = (field: {
    fieldName: string;
    fieldValue?: string;
    fieldSpecialization?: string;
    type?: string;
  }): string => {
    let key = '';
    const isTier = field.fieldValue?.includes('Tier');
    const isHeader = field.type === 'header';
    if (isTier) {
      const costType = field.fieldValue?.includes('Mail')
        ? 'mailRxCost'
        : 'rxCost';
      const tierSegments: string[] = field.fieldValue?.split(' ') || [];
      const tierNum = tierSegments[tierSegments.length - 1];
      const tierKey = 'TIER_' + tierNum;
      key =
        RX_TIER_FIELD_KEYS?.[costType as keyof typeof RX_TIER_FIELD_KEYS]?.[
          tierKey as keyof typeof RX_TIER_FIELD_KEYS['rxCost']
        ];
    } else if (isHeader) {
      key = HEADER_KEYS[field.fieldValue as keyof typeof HEADER_KEYS];
    } else {
      key = FIELD_KEYS[field.fieldName as keyof typeof FIELD_KEYS];
    }

    if (!key) {
      return field.fieldValue || '';
    }

    return getText(key, language);
  };

  const getTitlesAndKeys: any = () => {
    if (planType) {
      const fieldGroups = metadata.benguideMetaData.planFieldGroupings.find(
        function (planFieldGroup) {
          return planFieldGroup.planType === planType;
        }
      ) as any;

      const filteredGroups = [...fieldGroups.groups].splice(
        1,
        fieldGroups.groups.length
      );

      const titles = [] as any[];
      const tempKeys = [] as any[];
      filteredGroups.forEach((fieldGroup: any) => {
        const titleGroup = {} as any;
        const keyGroup = [] as any;

        titleGroup.sectionName = fieldGroup.sectionName;
        titleGroup.fields = [];

        fieldGroup.fields.forEach((e: any) => {
          const inNetworkValue = formatFieldValue(
            false,
            e,
            'InNetwork',
            selectedRate,
            plan,
            appendSuperScripts,
            clearSuperScripts,
            false,
            true,
            false
          );
          const outOfNetworkValue = formatFieldValue(
            false,
            e,
            'OutOfNetwork',
            selectedRate,
            plan,
            appendSuperScripts,
            clearSuperScripts,
            false,
            true,
            false
          );

          if (!(inNetworkValue === '-' && outOfNetworkValue === '-')) {
            const inNetworkTitle = formatFieldValue(
              false,
              e,
              'InNetwork',
              selectedRate,
              plan,
              appendSuperScripts,
              clearSuperScripts,
              true
            );
            const outOfNetworkTitle = formatFieldValue(
              false,
              e,
              'OutOfNetwork',
              selectedRate,
              plan,
              appendSuperScripts,
              clearSuperScripts,
              true
            );

            titleGroup.fields.push(getTextByField(e));
            keyGroup.push({
              ...{
                ...e,
                fieldValue: getTextByField(e),
              },
              inNetworkTitle,
              inNetworkValue,
              outOfNetworkTitle,
              outOfNetworkValue,
            });
          }
        });

        if (keyGroup.length) {
          titles.push(titleGroup);
          tempKeys.push({
            sectionName: getTextByField({
              fieldValue: fieldGroup.sectionName?.toUpperCase(),
              type: 'header',
              fieldName: '',
            }).toUpperCase(),
            fields: keyGroup,
          });
        }
      });
      return [titles, tempKeys];
    }
  };

  const renderSBCNavLink = () => {
    // SBC For Medical
    if (
      planType &&
      planType === 'medical' &&
      plan &&
      plan.documents &&
      plan.documents.SBC
    ) {
      return (
        <a
          href={
            plan && plan.documents && plan.documents.SBC
              ? `${baseApi}/v1/plans/${
                  plan.id
                }/documents?documentType=SBC&benefitKind=${planType.toUpperCase()}&isOpen=${true}`
              : ''
          }
          target="_blank"
          rel="noopener noreferrer"
          className={styles.heapSeeSbc}
        >
          {getText(SEE_SBC, language)}
        </a>
      );
    }
  };

  const [titles, keys] = getTitlesAndKeys();
  const deviceWidth: number = window.innerWidth;

  return (
    // full - for full screen
    <TableContextProvider>
      <Drawer
        size="md"
        className={styles.planDetailsPopup}
        show={isdrawerOpen}
        onHide={onHide}
      >
        <Drawer.Header>
          <div className={styles.planDetailsHeader}>
            {deviceWidth < 1025 ? (
              <>
                <img
                  className={styles.backArrow}
                  onClick={onHide}
                  src={iconBack}
                />
                <div className={styles.planName}>
                  {plan.name}
                  <div className={styles.popover}>{plan.name}</div>
                </div>
                <hr className={styles.hrTag} />
              </>
            ) : (
              <div>
                <div className={styles.title}>
                  {plan.name}
                  <div className={styles.popover}>{plan.name}</div>
                </div>
                <hr className={styles.hrTag} />
              </div>
            )}
          </div>
        </Drawer.Header>
        <Drawer.Body className={styles.drawerBody}>
          <div className={styles.planDetailsPopupWrapper}>
            <div className={styles.inNetworkOutNetworkWrapper}>
              {titles && (
                <CardTitles
                  // titles={titles.splice(1, titles.length)}
                  keys={keys}
                  allowHeaderTitle={false}
                />
              )}
              {deviceWidth > 576 ? (
                <div className={styles.inOutNetworkPlanCard}>
                  {selectedRate && plan && networkType && (
                    <PlanCardBody mappings={keys} plan={plan} />
                  )}
                </div>
              ) : (
                <div className={styles.inOutNetworkPlanCard}>
                  {selectedRate && plan && networkType && (
                    <PlanCardBodyMobile mappings={keys} plan={plan} />
                  )}
                </div>
              )}
              <div className={styles.sbcLink}>{renderSBCNavLink()}</div>
            </div>
          </div>
        </Drawer.Body>
      </Drawer>
    </TableContextProvider>
  );
};

export default PlanDetailsPopup;
